// docs/36 + docs/78: how a band walks the World chart. A walk is a `party_walk` row (from, to,
// when it left) drawn along the seeded road route at `WALK_CELLS_PER_S`; the band goes single
// file along it, the leader at the head and each follower `BAND_GAP` behind on the same road.
// Pure (no React), so every client draws every hero at the same spot from the row and the clock.
import { pathLength, pointAlong, routeBetween, WALK_CELLS_PER_S, type Overworld as OverworldMap } from './overworld';

/**
 * A `party_walk` row as drawn. `lead` is the stretch of road from where the band stood when the
 * walk was redirected back to `from` (the row only knows places), so it turns round on the spot.
 */
export interface BandWalk { from: string; to: string; departed: number; lead?: Array<[number, number]>; tail?: Array<[number, number]> }
/**
 * docs/78 BW2: the road the band's line walks, the stretch the followers still have behind the
 * leader first (`tail`, kept from the walk it turned out of), then the walk's own route. The
 * leader sets out `tailLen` along it, each follower `BAND_GAP` further back.
 */
export function bandTrail(ow: OverworldMap, w: BandWalk): { route: Array<[number, number]>; tailLen: number; ms: number } | undefined {
  const plan = walkPlan(ow, w.from, w.to, w.lead);
  if (!plan) return undefined;
  return w.tail ? { route: [...w.tail, ...plan.route], tailLen: pathLength(w.tail), ms: plan.ms } : { route: plan.route, tailLen: 0, ms: plan.ms };
}
/** Cells of road between two heroes in a walking band, and how many heroes walk on the chart. */
export const BAND_GAP = 1.15;
export const BAND_SHOWN = 6;
/** How long a walk takes along the roads (ms), and its route (lead-in first). */
export function walkPlan(ow: OverworldMap, from: string, to: string, lead?: Array<[number, number]>): { route: Array<[number, number]>; ms: number; leadLen: number } | undefined {
  const road = routeBetween(ow, from, to);
  if (!road) return undefined;
  const route = lead ? [...lead, ...road] : road;
  return { route, ms: (pathLength(route) / WALK_CELLS_PER_S) * 1000, leadLen: lead ? pathLength(lead) : 0 };
}
/** The stretch of a polyline between two distances along it (reversed when `d1 < d0`). */
export function subPath(path: Array<[number, number]>, d0: number, d1: number): Array<[number, number]> {
  const lo = Math.min(d0, d1), hi = Math.max(d0, d1);
  const out: Array<[number, number]> = [];
  const at = (d: number): [number, number] => { const p = pointAlong(path, d); return [p.x, p.y]; };
  out.push(at(lo));
  let run = 0;
  for (let i = 1; i < path.length; i++) {
    run += Math.hypot(path[i]![0] - path[i - 1]![0], path[i]![1] - path[i - 1]![1]);
    if (run > lo && run < hi) out.push(path[i]!);
  }
  out.push(at(hi));
  return d1 < d0 ? out.reverse() : out;
}
/** Where a walk stands at `at`: the roads back to its `from` and on to its `to` (undefined once arrived). */
export function walkTurns(ow: OverworldMap, w: BandWalk, at: number): { from: Array<[number, number]>; to: Array<[number, number]> } | undefined {
  const plan = walkPlan(ow, w.from, w.to, w.lead);
  if (!plan || at - w.departed >= plan.ms) return undefined;
  const total = pathLength(plan.route);
  const d = Math.max(0, ((at - w.departed) / 1000) * WALK_CELLS_PER_S);
  return { from: subPath(plan.route, d, plan.leadLen), to: subPath(plan.route, d, total) };
}
// a walking hero's feet sit on the road's strip (docs/143 OW5: the art's roads are 5 px through the cell's
// middle); at rest the band stands beside its place's art, feet level with the art's foot
const ROAD_FEET = 0.14, KNOT_FEET = 0.42;
/** How far beside the place's art the nearest hero of a resting band stands, and the step to the next. */
const KNOT_OUT = 0.95, KNOT_STEP = 0.6;
/** Cells over which a hero eases between the knot by a node and the road (less on a short walk). */
const KNOT_BLEND = 1.25;
/** Bands resting at one place stand beside it in turn: right, left, then this much further out on each side. */
export const BAND_SPREAD = KNOT_STEP * BAND_SHOWN + 0.4;
const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
export interface LineSpot { x: number; y: number; dx: number; moving: boolean }
/**
 * Where hero `i` of the band in rest slot `slot` at node `at` stands (docs/143 OW1): beside the place's art,
 * never on it, the leader nearest, the rest in a loose row outward; the first band to the right, the next to
 * the left, then further out.
 */
export function knotSpot(at: { x: number; y: number; side?: 1 | -1 }, i: number, slot = 0): LineSpot {
  const side = (at.side ?? 1) * (slot % 2 === 0 ? 1 : -1), ring = Math.floor(slot / 2);
  return { x: at.x + side * (KNOT_OUT + ring * BAND_SPREAD + i * KNOT_STEP), y: at.y + KNOT_FEET - (i % 2) * 0.1, dx: 0, moving: false };
}
/**
 * Where a band rests at a place (docs/143 OW1): by its node, or, at a crossing, on the nearer bank at the
 * bridge's end, on the side away from the water, so nobody stands in the river.
 */
export function restPoint(ow: Pick<OverworldMap, 'nodes' | 'crossings' | 'land'>, key: string): { x: number; y: number; side?: 1 | -1 } | undefined {
  const n = ow.nodes[key];
  const axis = ow.crossings?.[key];
  if (!n || !axis) return n;
  const x0 = Math.floor(n.x), y0 = Math.floor(n.y), [dx, dy] = axis === 'ew' ? [1, 0] : [0, 1];
  const dry = (x: number, y: number) => { const c = ow.land[y]?.[x]; return c !== undefined && c !== '~'; };
  for (let d = 1; d < 8; d++) for (const s of [1, -1] as const) {
    if (!dry(x0 + dx * d * s, y0 + dy * d * s)) continue;
    return { x: n.x + dx * d * s, y: n.y + dy * d * s, side: axis === 'ew' ? s : 1 };
  }
  return n;
}

/**
 * docs/78 BW2: a band's line `walked` cells along its trail (the leader's distance, tail
 * included): each hero `BAND_GAP` behind the one before on the same road, easing off it into the
 * knot on `to` at the end and, unless the walk turned on the road, out of the knot on `from`.
 */
export function bandLine(trail: { route: ReadonlyArray<readonly [number, number]>; tailLen: number }, to: { x: number; y: number }, from: { x: number; y: number } | undefined, n: number, walked: number, slot = 0): LineSpot[] {
  const route = trail.route, len = pathLength(route);
  const head = route[0]!, end = route[route.length - 1]!;
  // each hero eases over at least as many cells as it stands off the road, so stepping out to the side of a place is
  // never faster than about twice a walk (less on a walk too short for it)
  const blendFor = (k: { x: number; y: number }, at: readonly [number, number]) => Math.min(Math.max(KNOT_BLEND, 1.25 * Math.hypot(k.x - at[0], k.y - at[1] - ROAD_FEET)), len / 2);
  const out: LineSpot[] = [];
  for (let i = 0; i < n; i++) {
    const di = walked - i * BAND_GAP, d = Math.max(0, Math.min(len, di));
    if (len < 0.01 || di >= len) { out.push(knotSpot(to, i, slot)); continue; }
    const p = pointAlong(route, d);
    const k = knotSpot(to, i, slot);
    let x = p.x, y = p.y + ROAD_FEET;
    const e = clamp01(1 - (len - d) / blendFor(k, end));
    x += (k.x - end[0]) * e; y += (k.y - end[1] - ROAD_FEET) * e;
    if (!trail.tailLen && from) { const f = knotSpot(from, i), s = clamp01(1 - d / blendFor(f, head)); x += (f.x - head[0]) * s; y += (f.y - head[1] - ROAD_FEET) * s; }
    const moving = di > 0;
    out.push({ x, y, dx: moving ? p.dx : 0, moving });
  }
  return out;
}
